/**
 * Reads the result of the optional LinkedIn sign-in out of the URL fragment.
 *
 * The backend redirects back to `…/#linkedin=<base64url json>` on success, or
 * `…/#auth_error=…&auth_message=…` on failure. A fragment rather than a query
 * string because fragments are never sent to a server: the identity payload stays
 * out of access logs, proxy logs, and `Referer` headers.
 *
 * Never throws. A truncated or hand-edited fragment is a `null`, not a crash.
 */

export type AuthReturn =
  | { kind: 'identity'; name: string | null; email: string | null }
  | { kind: 'error'; message: string };

export function readAuthReturn(hash: string): AuthReturn | null {
  const fragment = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!fragment) return null;

  let params: URLSearchParams;
  try {
    params = new URLSearchParams(fragment);
  } catch {
    return null;
  }

  const error = params.get('auth_error');
  if (error) {
    return {
      kind: 'error',
      message:
        error === 'cancelled'
          ? 'Sign-in cancelled. Nothing changed.'
          : (params.get('auth_message') ?? 'Sign-in did not complete.'),
    };
  }

  const encoded = params.get('linkedin');
  if (!encoded) return null;

  try {
    const padded = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, '='));
    const parsed: unknown = JSON.parse(json);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const record = parsed as Record<string, unknown>;
    return {
      kind: 'identity',
      name: typeof record.name === 'string' ? record.name : null,
      email: typeof record.email === 'string' ? record.email : null,
    };
  } catch {
    return null;
  }
}
